import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  CallSplit as CallSplitIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ManageSearch as ManageSearchIcon,
  TravelExplore as TravelExploreIcon,
  Download as DownloadIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { monitoringAPI } from '../services/api';

const TIME_OPTIONS = [
  { label: '5m', minutes: 5 },
  { label: '10m', minutes: 10 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '6h', minutes: 360 },
  { label: '12h', minutes: 720 },
  { label: '24h', minutes: 1440 },
];

const DIVIDER_SX = { borderLeft: '1px solid rgba(255,255,255,0.08)' };
const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

// Categorize hangup causes for chip color.
function getCauseColor(cause) {
  if (!cause) return 'default';
  const c = String(cause).toUpperCase();
  if (c === 'NORMAL_CLEARING') return 'success';
  if (
    c === 'USER_BUSY' ||
    c === 'NO_ANSWER' ||
    c === 'NO_USER_RESPONSE' ||
    c === 'ORIGINATOR_CANCEL' ||
    c === 'NORMAL_TEMPORARY_FAILURE' ||
    c === 'ALLOTTED_TIMEOUT'
  ) return 'warning';
  if (
    c === 'CALL_REJECTED' ||
    c.startsWith('NO_ROUTE') ||
    c === 'NETWORK_OUT_OF_ORDER' ||
    c === 'DESTINATION_OUT_OF_ORDER' ||
    c === 'RECOVERY_ON_TIMER_EXPIRE' ||
    c === 'SERVICE_UNAVAILABLE' ||
    c === 'INCOMPATIBLE_DESTINATION' ||
    c === 'MEDIA_TIMEOUT'
  ) return 'error';
  return 'info';
}

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to = ((endEpoch || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

function extractAcdCallId(to) {
  if (!to) return null;
  const m = to.match(/^\*\*(\d+)\*/);
  return m ? m[1] : null;
}

function getMOSColor(mos) {
  if (!mos) return 'default';
  if (mos >= 4.0) return 'success';
  if (mos >= 3.5) return 'info';
  if (mos >= 3.0) return 'warning';
  return 'error';
}

function getPacketLossColor(loss) {
  if (loss === null || loss === undefined) return null;
  if (loss < 1) return 'success';
  if (loss < 3) return 'warning';
  if (loss < 5) return 'orange';
  return 'error';
}

function getMOSQualityText(mos) {
  if (!mos) return 'No data available';
  if (mos >= 4.0) return 'Good quality, most users satisfied';
  if (mos >= 3.5) return 'Fair, noticeable degradation but calls are comfortable enough for most users';
  if (mos >= 3.0) return 'Users start finding the call quality annoying or difficult';
  return 'Generally considered poor/unacceptable';
}

function formatTimestamp(epoch) {
  if (!epoch) return 'N/A';
  return new Date(epoch * 1000).toLocaleString();
}

function CauseChip({ cause }) {
  if (!cause) return <Typography variant="body2" color="text.secondary">-</Typography>;
  return (
    <Chip
      label={cause}
      size="small"
      color={getCauseColor(cause)}
      sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
    />
  );
}

function fmtCount(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

function fmtPct(p) {
  if (p == null) return '-';
  return `${p.toFixed(2)}%`;
}

function LegCountCells({ leg, isFirst }) {
  const firstCellSx = isFirst ? DIVIDER_SX : {};
  const empty = !leg || leg.count == null;
  return (
    <>
      <TableCell align="right" sx={{ ...firstCellSx, fontVariantNumeric: 'tabular-nums' }}>
        {empty ? <Typography variant="body2" color="text.secondary">-</Typography> : fmtCount(leg.count)}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {empty ? <Typography variant="body2" color="text.secondary">-</Typography> : (
          <Typography variant="body2" color="text.secondary">{fmtPct(leg.pct)}</Typography>
        )}
      </TableCell>
    </>
  );
}

function ReleaseCallsDialog({ open, cluster, cause, minutes, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleExport = () => {
    if (!calls.length) return;
    const headers = ['time', 'from', 'to', 'conference_name', 'switchname', 'duration', 'mos', 'jitter_ms', 'loss_pct', 'quality_pct', 'hangup_cause', 'call_id'];
    const csvRows = calls.map(c => [
      c.end_time ? new Date(c.end_time * 1000).toLocaleString() : '',
      c.from || '',
      c.to || '',
      c.conference_name || '',
      c.switchname || '',
      c.duration ?? '',
      c.mos ?? '',
      c.jitter ?? '',
      c.packet_loss ?? '',
      c.quality_pct ?? '',
      c.hangup_cause || '',
      c.call_id || '',
    ]);
    const csv = [headers, ...csvRows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calls-${cluster}-${cause}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!open || !cluster || !cause) return;
    setLoading(true);
    setError(null);
    setCalls([]);
    monitoringAPI.getRoutingQualityCauseCalls(cluster, cause, minutes)
      .then(res => setCalls(res.data.calls || []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load calls for release cause'))
      .finally(() => setLoading(false));
  }, [open, cluster, cause, minutes]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Calls for Release Cause - <CauseChip cause={cause} /> - {cluster}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Conference</TableCell>
                <TableCell>Media Node</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">MOS</TableCell>
                <TableCell align="center">Jitter</TableCell>
                <TableCell align="center">Loss%</TableCell>
                <TableCell align="center">Quality</TableCell>
                <TableCell>Release</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call) => {
                const homerUrl = buildHomerUrl(call.call_id, call.start_time, call.end_time);
                return (
                  <TableRow
                    key={call.call_id || call.uuid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/call-detail/${call.call_id}`)}
                  >
                    <TableCell>{formatTimestamp(call.end_time)}</TableCell>
                    <TableCell>{call.from || 'N/A'}</TableCell>
                    <TableCell>
                      {(() => {
                        const acdId = extractAcdCallId(call.to);
                        if (acdId && cluster) {
                          return (
                            <a
                              href={`https://${cluster}.singlecomm.com/supervisor/call.sc?id=${acdId}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#90caf9', textDecoration: 'none', fontSize: '0.82rem' }}
                            >
                              {call.to}
                            </a>
                          );
                        }
                        return call.to || 'N/A';
                      })()}
                    </TableCell>
                    <TableCell>{call.conference_name || 'N/A'}</TableCell>
                    <TableCell>{call.switchname || 'N/A'}</TableCell>
                    <TableCell align="center">{call.duration || 0}s</TableCell>
                    <TableCell align="center">
                      {call.mos ? (
                        <Tooltip title={getMOSQualityText(call.mos)} arrow>
                          <Chip label={call.mos.toFixed(2)} size="small" color={getMOSColor(call.mos)} />
                        </Tooltip>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {call.jitter ? `${call.jitter.toFixed(1)}ms` : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {call.packet_loss !== null ? (() => {
                        const color = getPacketLossColor(call.packet_loss);
                        if (color === 'orange') {
                          return (
                            <Chip
                              label={`${call.packet_loss.toFixed(2)}%`}
                              size="small"
                              sx={{ backgroundColor: '#ed6c02', color: '#fff' }}
                            />
                          );
                        }
                        return color ? (
                          <Chip
                            label={`${call.packet_loss.toFixed(2)}%`}
                            size="small"
                            color={color}
                          />
                        ) : `${call.packet_loss.toFixed(2)}%`;
                      })() : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {call.quality_pct ? `${call.quality_pct.toFixed(1)}%` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip label={call.hangup_cause || 'UNKNOWN'} size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="RTCP Detail">
                        <IconButton
                          size="small"
                          component="a"
                          href={`/call-detail/${call.call_id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <TimelineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="CDR">
                        <IconButton
                          size="small"
                          component="a"
                          href={`/cdr/${call.call_id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ManageSearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {homerUrl && (
                        <Tooltip title="SIP Trace in Homer">
                          <IconButton
                            size="small"
                            component="a"
                            href={homerUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TravelExploreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {call.conference_name && cluster && (
                        <Tooltip title="Conference Info">
                          <IconButton
                            size="small"
                            component="a"
                            href={`https://${cluster}.singlecomm.com/supervisor/call.sc?id=${encodeURIComponent(call.conference_name)}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GroupsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!calls.length && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No calls found for this cause in the selected window.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, pl: 1.5 }}>
          Total calls: {fmtCount(calls.length)}
        </Typography>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={!calls.length}
          variant="outlined"
          size="small"
        >
          Export CSV
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RoutingQualityPage() {
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // { cluster, cause }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    monitoringAPI.getRoutingQualityCauses(minutes, 10)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load data'))
      .finally(() => setLoading(false));
  }, [minutes]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Routing Quality
        </Typography>
        <ToggleButtonGroup
          value={minutes}
          exclusive
          onChange={(_, v) => { if (v !== null) setMinutes(v); }}
          size="small"
        >
          {TIME_OPTIONS.map(o => (
            <ToggleButton key={o.minutes} value={o.minutes}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {data?.timestamp && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Last updated: {new Date(data.timestamp + 'Z').toLocaleString()}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && data?.clusters?.map(cluster => (
        <Card key={cluster.cluster} sx={{ mb: 3 }} component={Paper} elevation={2}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <CallSplitIcon color="primary" fontSize="small" />
              <Typography variant="h6">
                Cluster: <strong>{cluster.cluster}</strong>
              </Typography>
              <Chip
                label={`Top ${cluster.causes.length} cause${cluster.causes.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Totals - Agent: {fmtCount(cluster.total_calls.agent)}
                {' · '}Inbound: {fmtCount(cluster.total_calls.inbound)}
                {' · '}Outbound: {fmtCount(cluster.total_calls.outbound)}
              </Typography>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: 'bold',
                        verticalAlign: 'bottom',
                        borderRight: '1px solid rgba(255,255,255,0.08)',
                        minWidth: 240,
                      }}
                    >
                      Hangup Cause
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ color: 'primary.light', fontWeight: 'bold', borderBottom: 0 }}
                    >
                      Agent
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ color: 'success.light', fontWeight: 'bold', borderBottom: 0, ...DIVIDER_SX }}
                    >
                      Inbound
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ color: 'warning.light', fontWeight: 'bold', borderBottom: 0, ...DIVIDER_SX }}
                    >
                      Outbound
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="right" sx={{ fontSize: '0.72rem' }}>Count</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem' }}>%</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem', ...DIVIDER_SX }}>Count</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem' }}>%</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem', ...DIVIDER_SX }}>Count</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem' }}>%</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {cluster.causes.map(row => (
                    <Tooltip
                      key={row.cause}
                      title="Click to view matching calls"
                      placement="left"
                      arrow
                    >
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelected({ cluster: cluster.cluster, cause: row.cause })}
                      >
                        <TableCell sx={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                          <CauseChip cause={row.cause} />
                        </TableCell>
                        <LegCountCells leg={row.agent} />
                        <LegCountCells leg={row.inbound} isFirst />
                        <LegCountCells leg={row.outbound} isFirst />
                      </TableRow>
                    </Tooltip>
                  ))}

                  {cluster.causes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No hangup causes in this window
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      ))}

      {!loading && !error && data?.clusters?.length === 0 && (
        <Alert severity="info">
          No data available for the selected time window.
        </Alert>
      )}

      <ReleaseCallsDialog
        open={!!selected}
        cluster={selected?.cluster}
        cause={selected?.cause}
        minutes={minutes}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
